import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { DOZEN_API_URL, IS_LOCAL_DEV } from '../config';
import AdminShell from './AdminShell';
import ScheduleSettings from './ScheduleSettings';
import { formatCountdown, formatPuzzleDate } from '../utils/dozen';
import './AdminPanel.css';
import './DozenAdmin.css';

const ADMIN_URL = `${DOZEN_API_URL}/admin`;

function formatIpm(ipm) {
  if (ipm === null || ipm === undefined) return 'не в списке';
  return `${ipm} на млн`;
}

function WordRow({ entry, editable, busy, onReshuffle, onReplace }) {
  return (
    <li className="dozen-admin-row">
      <span className="dozen-admin-row__len">{entry.length}</span>
      <div className="dozen-admin-row__main">
        <div className="dozen-admin-row__word">
          <strong>{entry.word}</strong>
          <span className="dozen-admin-row__letters">{entry.letters}</span>
        </div>
        <div className="dozen-admin-row__meta">
          {formatIpm(entry.ipm)}
          {entry.alternatives.length > 0 && ` · также: ${entry.alternatives.join(', ')}`}
        </div>
      </div>
      {editable && (
        <div className="dozen-admin-row__actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onReshuffle(entry.position)}
            disabled={busy}
            title="Перемешать буквы"
          >
            Перемешать
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onReplace(entry.position)}
            disabled={busy}
            title="Случайное другое слово"
          >
            Другое
          </button>
        </div>
      )}
    </li>
  );
}

function DozenAdmin() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${ADMIN_URL}/overview`);
      setOverview(data);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Ошибка загрузки' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const run = async (path, body, successText) => {
    setBusy(true);
    setMessage(null);
    try {
      const { data } = await axios.post(`${ADMIN_URL}/${path}`, body);
      setOverview(data);
      if (successText) setMessage({ type: 'success', text: successText });
      return true;
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Ошибка' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleReplace = (position) => run(`next/words/${position}/replace`, {}, null);
  const handleReshuffle = (position) => run(`next/words/${position}/reshuffle`, {}, null);
  const handleRegenerate = () => run('next/regenerate', {}, 'Собрана новая дюжина');
  const handlePublish = async () => {
    setConfirmPublish(false);
    await run('next/publish', {}, 'Опубликовано — игроки видят новую дюжину');
  };

  const shell = (children) => (
    <AdminShell section="dozen">
      {children}
    </AdminShell>
  );

  if (loading) return shell(<div className="loading">Загрузка...</div>);

  const next = overview?.next;
  const current = overview?.current;
  const nextMs = overview?.nextPuzzleTime
    ? new Date(overview.nextPuzzleTime).getTime() - now
    : 0;

  return shell(
    <div className="admin-panel">
      {message && (
        <div className={`alert ${message.type === 'error' ? 'alert-error' : 'alert-success'}`}>
          {message.text}
        </div>
      )}

      <div className="admin-section panel">
        <h2 className="section-title">Следующая дюжина</h2>
        <p className="meta-line">
          Выйдет при следующей публикации
          {nextMs > 0 ? ` — через ${formatCountdown(nextMs)}` : ''}
        </p>
        <div className="btn-row">
          <button type="button" className="btn btn-primary" onClick={handleRegenerate} disabled={busy}>
            Новые 12 слов
          </button>
          {IS_LOCAL_DEV && (
            <button
              type="button"
              className="btn btn-success"
              onClick={() => setConfirmPublish(true)}
              disabled={busy}
            >
              Опубликовать сейчас
            </button>
          )}
        </div>

        {IS_LOCAL_DEV && confirmPublish && (
          <div className="alert alert-warn confirm-dialog">
            <p>
              <strong>Опубликовать эту дюжину сейчас?</strong>
            </p>
            <p>Текущая дюжина закроется для новых игроков. Начатые игры доиграются.</p>
            <div className="btn-row">
              <button type="button" className="btn btn-success" onClick={handlePublish}>
                Да, опубликовать
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmPublish(false)}>
                Отмена
              </button>
            </div>
          </div>
        )}

        {next ? (
          <ol className="dozen-admin-list">
            {next.words.map((entry) => (
              <WordRow
                key={`${next.id}-${entry.position}`}
                entry={entry}
                editable
                busy={busy}
                onReshuffle={handleReshuffle}
                onReplace={handleReplace}
              />
            ))}
          </ol>
        ) : (
          <p className="meta-line">Не удалось загрузить</p>
        )}
      </div>

      <div className="admin-section panel">
        <h2 className="section-title">Текущая дюжина</h2>
        {current ? (
          <>
            <p className="meta-line">
              Опубликована {formatPuzzleDate(current.publishedAt)}
              {overview.players
                ? ` · начали ${overview.players.started}, закончили ${overview.players.finished}`
                : ''}
            </p>
            <ol className="dozen-admin-list">
              {current.words.map((entry) => (
                <WordRow key={`${current.id}-${entry.position}`} entry={entry} editable={false} />
              ))}
            </ol>
          </>
        ) : (
          <p className="meta-line">Ещё не опубликована</p>
        )}
      </div>

      <ScheduleSettings scheduleUrl={`${ADMIN_URL}/schedule`} onSaved={load} />
    </div>
  );
}

export default DozenAdmin;
